import React from "react";

import { useRentalInvoicePreviewContext } from "./RentalInvoicePreviewContainer";
import { UNCATEGORIZED_LABEL } from "./categorizeItems";
import { pillLabel } from "./statusLabels";
import { formatCurrency } from "./currency";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const RentalInvoicePreview = () => {
  const { rentalInvoice, calendarEvent } = useRentalInvoicePreviewContext();

  const subtotal = rentalInvoice.subtotal || 0;
  const discount = rentalInvoice.discount || 0;
  const depositPaid = rentalInvoice.deposit_paid_amount || 0;
  const taxFees = rentalInvoice.rental_invoice_tax_fees || [];
  const taxFeeTotal = taxFees.reduce((sum, fee) => sum + Number(fee.calculated_amount || 0), 0);
  const grandTotal = rentalInvoice.grand_total ?? (subtotal - discount + taxFeeTotal);
  const remainingBalance = rentalInvoice.remaining_balance ?? Math.max(0, grandTotal - depositPaid);
  const categorizedItems = (rentalInvoice.categorized_items || [])
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => (item.title || "").trim().length > 0),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div style={{"padding": "0px 7px"}}>
      <table style={{ "width": "100%", "fontFamily": "Open Sans", "fontSize": "14px" }}>
        <tbody>
          <tr>
            {calendarEvent && calendarEvent.venue_ownership && calendarEvent.venue_ownership.logo_thumb_url ? (
              <td style={{ verticalAlign: "top", width: "56px", padding: "0 16px 16px 0" }}>
                <div style={{
                  width: "56px",
                  height: "56px",
                  overflow: "hidden",
                  borderRadius: "8px"
                }}>
                  <img src={calendarEvent.venue_ownership.logo_thumb_url} style={{ height: "100%" }} />
                </div>
              </td>
            ) : null}
            <td style={{ verticalAlign: "top", padding: "0 16px 16px 0" }}>
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr>
                    <td>
                      <p style={{ margin: "0 0 0 0", fontSize: "16px" }}>
                        <strong>{rentalInvoice.title}</strong>
                      </p>
                    </td>
                    <td style={{ textAlign: "right", verticalAlign: "top" }}>
                      <div style={{
                        display: "inline-block",
                        fontSize: "22px",
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        background: "#FAFAFA",
                        border: "1px solid #E9EAEB",
                        borderRadius: "24px",
                        padding: "10px 24px"
                      }}>
                        <span>{pillLabel(rentalInvoice.status)}</span>
                      </div>
                    </td>
                  </tr>
                  {calendarEvent ? (
                    <>
                      <tr>
                        <td colSpan={2}>
                          {calendarEvent.type === "Confirm" ? (
                            <p style={{ margin: "0 0 0 0", fontSize: "12px" }}>
                              <strong>
                                {(new Date(calendarEvent.hold_positions[0].start_time)).toLocaleDateString("en-US", {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  timeZone: calendarEvent.venue_ownership.tzinfo_identifier
                                })}
                              </strong>
                            </p>
                          ) : null}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <p style={{ margin: "0 0 0 0", fontSize: "12px" }}>{calendarEvent.venue.name}{calendarEvent.venue.address_from_components ? ` - ${calendarEvent.venue.address_from_components}` : ""}</p>
                        </td>
                      </tr>
                    </>
                  ) : null}
                </tbody>
              </table>
            </td>
          </tr>
          {categorizedItems.length > 0 ? (
            <>
              <table style={{"width": "100%", "borderSpacing": "0px 2px", "borderCollapse": "separate"}}>
                <tr>
                  <td />
                  <td>
                    <p style={{"fontSize": "14px", "color": "#717680", "margin": "0px", "paddingRight": "14px", "textAlign": "right"}}>RATE</p>
                  </td>
                  <td>
                    <p style={{"fontSize": "14px", "color": "#717680", "margin": "0px", "paddingRight": "14px", "textAlign": "right"}}>QTY</p>
                  </td>
                  <td>
                    <p style={{"fontSize": "14px", "color": "#717680", "margin": "0px", "paddingRight": "14px", "textAlign": "right"}}>TOTAL</p>
                  </td>
                </tr>
                {categorizedItems.map((group, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    {group.category !== UNCATEGORIZED_LABEL && (
                      <tr>
                        <td colSpan={4} style={{
                          padding: "16px 0px 6px 0px",
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: "#414651",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}>
                          {group.category}
                        </td>
                      </tr>
                    )}
                    {group.items.map((item) => (
                      <tr key={item.id} style={{ "background": "#F2F2F2"}}>
                        <td style={{"padding": "4px 16px"}}>
                          <p style={{"fontSize": "14px", "margin": "0px"}}>{item.title}</p>
                          {item.description ? (
                            <p style={{"fontSize": "12px", "margin": "0px", "color": "#717680"}}>{item.description}</p>
                          ) : null}
                        </td>
                        <td style={{"padding": "4px 16px"}}>
                          <p style={{"fontSize": "14px", "margin": "0px", "textAlign": "right"}}>{formatCurrency(item.cost)}</p>
                        </td>
                        <td style={{"padding": "4px 16px"}}>
                          <p style={{"fontSize": "14px", "margin": "0px", "textAlign": "right"}}>{item.quantity}</p>
                        </td>
                        <td style={{"padding": "4px 16px"}}>
                          <p style={{"fontSize": "14px", "margin": "0px", "textAlign": "right"}}><strong>{formatCurrency(item.total)}</strong></p>
                        </td>
                      </tr>
                    ))}
                    {group.category !== UNCATEGORIZED_LABEL && (
                      <tr>
                        <td colSpan={3} style={{"textAlign": "right", "padding": "4px 16px"}}>
                          <em style={{"fontSize": "13px", "color": "#414651"}}>Subtotal</em>
                        </td>
                        <td style={{"textAlign": "right", "padding": "4px 16px"}}>
                          <strong style={{"fontSize": "14px"}}>{formatCurrency(group.subtotal)}</strong>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr>
                  <td colSpan={3} style={{"textAlign": "right", "padding": "4px 16px"}}>
                    <strong style={{"fontSize": "16px"}}>Total</strong>
                  </td>
                  <td style={{"textAlign": "right", "padding": "4px 16px"}}>
                    <strong style={{"fontSize": "16px"}}>{formatCurrency(subtotal)}</strong>
                  </td>
                </tr>
              </table>
            </>
          ) : null}
          <div style={{ "display": "flex", "margin": "30px 0px" }}>
            {rentalInvoice.deposits.map((deposit) => (
              <div key={deposit.id} style={{
                background: '#f9f9f9',
                padding: '18px 15px',
                borderRadius: '4px',
                display: 'inline-block',
                fontSize: '14px',
                minWidth: '415px',
                color: '#333333',
                margin: "10px 0px"
              }}>
                <span style={{ fontSize: '12px', color: '#717680' }}>
                  DEPOSITS
                </span>
                <p style={{ fontSize: '14px', marginTop: '3px', marginBottom: '0px' }}>
                  <strong style={{ fontSize: '18px' }}>{formatCurrency(deposit.amount)}</strong> due on {formatDate(deposit.due_date)}
                </p>
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px", borderRadius: 8, border: "1px solid #E9EAEB", marginBottom: "16px" }}>
            <div style={{ fontSize: 18, color: "#414651", marginBottom: "16px" }}>
              <strong>Totals</strong>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                    Subtotal
                  </td>
                  <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                    {formatCurrency(subtotal)}
                  </td>
                </tr>
                {discount > 0 && (
                  <tr>
                    <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                      Discount
                    </td>
                    <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                      {formatCurrency(discount)}
                    </td>
                  </tr>
                )}
                {taxFees.map((taxFee) => (
                  <tr key={taxFee.id}>
                    <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                      {taxFee.name || "Tax/Fee"} ({parseFloat(taxFee.rate)}%)
                    </td>
                    <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                      {formatCurrency(taxFee.calculated_amount)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}>
                    <hr style={{ margin: "16px 0", border: "1px solid #E9EAEB", width: "100%" }} />
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                    <strong>Total Due</strong>
                  </td>
                  <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                    <strong>{formatCurrency(grandTotal)}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                    Deposit Paid
                  </td>
                  <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                    {formatCurrency(depositPaid)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 14, color: "#333333", padding: "4px 0" }}>
                    Balance Remaining
                  </td>
                  <td style={{ fontSize: 14, color: "#333333", textAlign: "right", padding: "4px 0" }}>
                    {formatCurrency(remainingBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {rentalInvoice.notes ? (
            <div style={{ padding: "16px 24px", borderRadius: 8, border: "1px solid #E9EAEB", marginBottom: "16px" }}>
              <div style={{ fontSize: 18, color: "#414651", marginBottom: "16px" }}>
                <strong>Notes</strong>
              </div>
              <div style={{ fontSize: 14, color: "#333333" }}>
                <div dangerouslySetInnerHTML={{__html: rentalInvoice.sanitized_notes}} />
              </div>
            </div>
          ) : null}
          {rentalInvoice.terms_and_conditions ? (
            <div style={{ padding: "16px 24px", borderRadius: 8, border: "1px solid #E9EAEB", marginBottom: "16px" }}>
              <div style={{ fontSize: 18, color: "#414651", marginBottom: "16px" }}>
                <strong>Terms & Conditions</strong>
              </div>
              <div style={{ fontSize: 14, color: "#333333" }}>
                <div dangerouslySetInnerHTML={{__html: rentalInvoice.sanitized_terms_and_conditions}} />
              </div>
            </div>
          ) : null}
        </tbody>
      </table>
      <div className="mt-3">
        <p>
          <strong>
            It is confirmed that we have read, understood and approve of the terms and conditions set forth here.
          </strong>
        </p>
        <table style={{width: "100%", fontSize: "14px"}}>
          <colgroup>
            <col style={{width: "50%"}} />
            <col style={{width: "50%"}} />
          </colgroup>
          <tbody>
            <tr>
              <td>
                <div style={{paddingRight: "10px"}}>
                  <label>Name / Company</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
              <td>
                <div style={{paddingLeft: "10px"}}>
                  <label>Name / Company</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div style={{paddingRight: "10px"}}>
                  <label>Phone / Email</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
              <td>
                <div style={{paddingLeft: "10px"}}>
                  <label>Phone / Email</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div style={{paddingRight: "10px"}}>
                  <label>Signature</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
              <td>
                <div style={{paddingLeft: "10px"}}>
                  <label>Signature</label>
                  <input
                    type="text"
                    style={{
                      marginBottom: "10px",
                      width: "100%",
                      height: "38px",
                      border: "none",
                      borderBottom: "1px solid #a0a0a0",
                    }}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RentalInvoicePreview;
